/**
 * Inside Man (04 §8): "One gate stands unlocked and one guard is absent."
 *
 * ROUND5 found the card doing nothing at all for ten RP - the dearest of the nine. There is no lock
 * anywhere in the sim (a gate on these maps is a hole in the perimeter, not an object), so "unlocked"
 * has to be a hole that would not otherwise be there, and it is:
 *
 *   - a compound with a perimeter gets its NORTH face opened. The planner only ever cuts a south gate
 *     and one flank gate, so the back of the compound is solid on every other mission; the man on the
 *     inside has left the one door the garrison does not watch.
 *   - a mission with no compound perimeter - or the one sealed archetype, whose whole point is that it
 *     must be cut into - gets a fourth crossing in the belt wire instead, away from the ones everybody
 *     knows about. The card is worth the same ten RP on all thirty-six archetypes or it is worth none.
 *
 * The gap is kept off `plan.gates` on purpose: posts.ts, emplacements.ts and roads.ts all walk that list
 * and would post a sentry on it, site a gun to cover it and run a road up to it. It is reserved `keep`
 * here instead, so scatter, mines and ponds stay out of the lane.
 *
 * The absent guard is the sentry standing nearest that gap, and the two halves are one sentence: the
 * gate is open and the man who watched it is not there. `MissionDef.openGate` carries the place to the
 * briefing and the strip map, because a way in nobody can find is another card that does nothing.
 */

import { collidersFor, PROPS } from '../sim/props';
import type { MissionDef, SpawnDef } from '../sim/types';
import { FLAG } from './grid';
import { COMPOUND, LINES, LIMITS, MAP } from './tuning';
import type { Gate, GenCtx } from './types';

/** Clear ground kept round the open gate, as `reserveKeyGround` keeps it round the planner's own gates. */
const GATE_KEEP = 1.5;

/** A knife at a man this close to an inhabited building wins his uniform (`archetypes/helpers.uniformDonor`). */
const DONOR_REACH = 4.5;

/** Where the belt's fourth crossing may be cut: in from the edge, so it is a lane and not a corner. */
const BELT_MARGIN = 8;

/** The one gate the inside man has left open, or null when the card is not held. */
function cutGate(ctx: GenCtx): Gate | null {
  const { spec, plan } = ctx;
  const rng = ctx.rng.fork('inside-man');
  if (spec.perimeter !== 'none' && !spec.sealed && spec.layout === 'compound') {
    const c = plan.compound;
    const mid = (c.x0 + c.x1) / 2;
    const half = COMPOUND.sideGateWidth / 2;
    const x = Math.max(c.x0 + half + 1, Math.min(c.x1 - half - 1, mid + rng.range(-6, 6)));
    return { side: 'north', x: Math.round(x * 2) / 2, z: c.z0, width: COMPOUND.sideGateWidth };
  }
  // No perimeter to open, or a perimeter that must stay sealed: the belt wire gets the extra crossing.
  const belt = plan.lines[plan.lines.length - 1];
  if (belt === undefined) return null;
  // As far from every crossing the garrison already watches as the width of the map allows.
  let best = { x: 0, away: -1 };
  for (let x = MAP.edge + BELT_MARGIN; x <= ctx.sizeX - MAP.edge - BELT_MARGIN; x += 1) {
    let away = Infinity;
    for (const g of belt.gaps) away = Math.min(away, Math.abs(x - g.x));
    if (away > best.away) best = { x, away };
  }
  if (best.away < LINES.minCrossingSpacing) return null;
  const gate: Gate = { side: 'south', x: best.x, z: belt.z, width: LINES.extraGap };
  belt.gaps.push({ x: gate.x, width: gate.width });
  return gate;
}

/**
 * Open the gate. Runs before anything is built, so the perimeter and the belt are laid with the gap in
 * them from the first - a hole cut afterwards would leave the props it removed already on the map.
 */
export function openInsideGate(ctx: GenCtx): void {
  if (!ctx.params.intel.includes('inside-man')) return;
  const gate = cutGate(ctx);
  // A card that was paid for and quietly did nothing is the whole of what this round is about: rather
  // re-roll the map, as the field grave does, than hand out a gate that is not there.
  if (gate === null) {
    ctx.map.problems.push('inside man: nowhere to leave a gate open');
    return;
  }
  ctx.plan.insideGate = gate;
  ctx.map.mask.circle(gate.x, gate.z, gate.width / 2 + GATE_KEEP, FLAG.keep);
}

/** Is this man close enough to an inhabited building that he is somebody's uniform? Then he stays. */
function isUniformDonor(ctx: GenCtx, spawn: SpawnDef): boolean {
  for (const prop of ctx.map.props) {
    if (PROPS[prop.kind].shelter !== true) continue;
    for (const c of collidersFor(prop)) {
      const dx = Math.max(0, Math.abs(spawn.x - c.x) - c.hx - c.r);
      const dz = Math.max(0, Math.abs(spawn.z - c.z) - c.hz - c.r);
      if (dx * dx + dz * dz <= DONOR_REACH * DONOR_REACH) return true;
    }
  }
  return false;
}

/**
 * Take the absent guard off the map. Runs after the garrison is bought AND after the minimum top-up, or
 * the top-up would simply buy him again somewhere else and nobody would be absent at all.
 *
 * Who is safe to lose is decided by what the archetypes assert about their garrisons: never a gunner in
 * an emplacement (two archetypes require exactly one crew), never a man on a patrol (several require a
 * walked beat), never the officer or the man the mission is about, never a soldier who is not there yet,
 * never the last of his kind, and never the man whose uniform a mission needs.
 */
export function dismissAbsentGuard(ctx: GenCtx): boolean {
  const gate = ctx.plan.insideGate;
  if (gate === null) return false;
  // A garrison already at the floor cannot lose a man: `validate/fairness.ts` wants ten of them.
  if (ctx.map.spawns.length <= LIMITS.spawnsMin) {
    ctx.map.problems.push('inside man: the garrison is too small to spare the absent guard');
    return false;
  }
  const counts = new Map<string, number>();
  for (const s of ctx.map.spawns) counts.set(s.kind, (counts.get(s.kind) ?? 0) + 1);
  let pick = -1;
  let bestAway = Infinity;
  ctx.map.spawns.forEach((s, i) => {
    if (s.kind !== 'rifleman' && s.kind !== 'elite') return;
    if (s.patrol !== undefined || s.mans !== undefined || s.dormantUntil !== undefined) return;
    if (s.captureTarget === true || s.objectiveTarget === true || s.unarmed === true) return;
    if ((counts.get(s.kind) ?? 0) < 2 || isUniformDonor(ctx, s)) return;
    const away = (s.x - gate.x) * (s.x - gate.x) + (s.z - gate.z) * (s.z - gate.z);
    if (away >= bestAway) return;
    bestAway = away;
    pick = i;
  });
  // Nobody could be spared without breaking something a mission asserts about its own garrison. Try a
  // different map first; if every map says the same, `openGate.guardGone` is false and the standing
  // orders say so in words (`ui/hud/missionConditions.intelOrders`) rather than promising a man.
  if (pick < 0) {
    ctx.map.problems.push('inside man: no sentry could be spared as the absent guard');
    return false;
  }
  ctx.map.spawns.splice(pick, 1);
  return true;
}

/** What the mission carries out of all this: where the gate is, and whether its man went with it. */
export function openGateOf(ctx: GenCtx, guardGone: boolean): MissionDef['openGate'] {
  const gate = ctx.plan.insideGate;
  return gate === null ? undefined : { x: gate.x, z: gate.z, width: gate.width, guardGone };
}
